import React from 'react';
import { cn } from '../../utils/cn';

// Hover effect shared by every button: a colour layer rises from the bottom.
// It's a ::before layer scaled on Y from 0 to 1, so only `transform` animates. The GPU
// composites that without any re-layout, keeping it smooth on slow PCs. `isolate` keeps the
// layer above the button's own background but below its label. Each variant sets the layer
// colour with `before:bg-*`. Reduced-motion users get an instant change (see index.css).
export const fillClass = cn(
    'relative isolate overflow-hidden',
    'before:absolute before:inset-0 before:-z-10 before:origin-bottom before:scale-y-0',
    'before:transition-transform before:duration-300 before:ease-[cubic-bezier(0.22,1,0.36,1)]',
    'hover:before:scale-y-100 focus-visible:before:scale-y-100'
);

const VARIANTS = {
    primary: 'bg-brand-600 text-white shadow-sm before:bg-brand-900 dark:before:bg-brand-500',
    secondary:
        'bg-white text-slate-700 shadow-sm ring-1 ring-inset ring-slate-200 before:bg-brand-50 hover:text-brand-700 hover:ring-brand-200 ' +
        'dark:bg-slate-900 dark:text-slate-200 dark:ring-slate-700 dark:before:bg-brand-500/15 dark:hover:text-brand-300 dark:hover:ring-brand-500/40',
    ghost: 'text-slate-600 before:bg-slate-100 hover:text-slate-900 dark:text-slate-300 dark:before:bg-slate-800 dark:hover:text-white',
    danger: 'bg-rose-600 text-white shadow-sm before:bg-rose-800 dark:before:bg-rose-500',
    'danger-soft':
        'bg-rose-50 text-rose-600 before:bg-rose-100 hover:text-rose-700 dark:bg-rose-500/10 dark:text-rose-400 dark:before:bg-rose-500/20 dark:hover:text-rose-300',
};

const SIZES = {
    sm: 'h-8 gap-1.5 px-3 text-xs',
    md: 'h-10 gap-2 px-4 text-sm',
    lg: 'h-11 gap-2 px-5 text-sm',
    icon: 'h-10 w-10 text-sm',
};

export const Spinner = ({ className }) => (
    <span
        aria-hidden="true"
        className={cn('inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent', className)}
    />
);

function Button({
    variant = 'primary',
    size = 'md',
    icon: Icon,
    iconRight: IconRight,
    loading = false,
    disabled,
    type = 'button',
    className,
    children,
    ...props
}) {
    return (
        <button
            type={type}
            disabled={disabled || loading}
            className={cn(
                'inline-flex shrink-0 select-none items-center justify-center whitespace-nowrap rounded-lg font-semibold',
                'transition-[color,box-shadow,transform] duration-300 active:scale-[0.97]',
                'disabled:pointer-events-none disabled:opacity-60',
                fillClass,
                VARIANTS[variant],
                SIZES[size],
                className
            )}
            {...props}
        >
            {loading ? <Spinner /> : Icon && <Icon className="h-4 w-4" aria-hidden="true" />}
            {children}
            {IconRight && <IconRight className="h-4 w-4" aria-hidden="true" />}
        </button>
    );
}

const ICON_TONES = {
    neutral:
        'text-slate-400 before:bg-slate-100 hover:text-slate-700 dark:text-slate-500 dark:before:bg-slate-800 dark:hover:text-slate-200',
    brand: 'text-brand-600 before:bg-brand-50 hover:text-brand-700 dark:text-brand-400 dark:before:bg-brand-500/15 dark:hover:text-brand-300',
    danger: 'text-rose-600 before:bg-rose-50 hover:text-rose-700 dark:text-rose-400 dark:before:bg-rose-500/15 dark:hover:text-rose-300',
};

// Small icon-only action (edit, delete, close). Uses `title` as the accessible name if no
// aria-label is given, since there's no visible text.
export function IconButton({ tone = 'neutral', type = 'button', className, children, ...props }) {
    return (
        <button
            type={type}
            aria-label={props['aria-label'] ?? props.title}
            className={cn(
                'inline-flex items-center justify-center rounded-lg p-2',
                'transition-[color,transform] duration-300 active:scale-95 disabled:pointer-events-none disabled:opacity-50',
                fillClass,
                ICON_TONES[tone],
                className
            )}
            {...props}
        >
            {children}
        </button>
    );
}

export default Button;
